import { getGroupedSegments, processGroupedSegments } from '../utils/legacyGeoJSONConversions';
import type { ActivityWithTracks, MapWithRoutes } from '../types/Geo';

type MapWithIdentifiers = { type?: string; trailId?: number };

/**
 * Calculates the total distance of a map, optionally factoring in elevation changes (3D distance).
 *
 * @param map - The map object (trail, route, or activity)
 * @param experimentFlagEnabled - Whether the 3D distance experiment flag is enabled.
 *                                 If true AND the map is a verified trail, uses 3D distance.
 *                                 Otherwise, uses 2D distance.
 * @returns Total distance in meters, or 0 if no valid segments
 */
const getMap3DDistance = (map: Partial<ActivityWithTracks> | Partial<MapWithRoutes>, experimentFlagEnabled?: boolean): number => {
  const groupedSegments = getGroupedSegments(map);
  const segments = groupedSegments?.map(group => group.segments).flat();

  if (!segments || segments.length < 1) {
    return 0;
  }

  // Determine whether to use 3D distance
  // BOTH conditions must be true:
  // 1. Experiment flag must be enabled (experimentFlagEnabled === true)
  // 2. Map must be a verified trail (type === 'AtVerifiedMap' OR has trailId)
  const mapWithIdentifiers = map as MapWithIdentifiers;
  const isVerifiedTrail = mapWithIdentifiers.type === 'AtVerifiedMap' || !!mapWithIdentifiers.trailId;
  const use3DDistance = experimentFlagEnabled === true && isVerifiedTrail;

  const { totalDistance } = processGroupedSegments(groupedSegments, false, use3DDistance);
  return totalDistance;
};

export default getMap3DDistance;
